import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { getLevel } from "@/lib/plans";
import { api } from "@/lib/api";
import {
  Barbell, ForkKnife, Pill, ListChecks, BowlFood, FileText, Sparkle,
  ArrowRight, CheckCircle, Warning, DownloadSimple, Lock, CircleNotch, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { Barbell, ForkKnife, Pill, ListChecks, Bowl: BowlFood, BowlFood, FileText };

export default function MyLibrary() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const level = getLevel(user);
  const canAccess = level >= 2;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      nav("/login");
      return;
    }
    if (!canAccess) {
      setLoading(false);
      return;
    }
    api
      .get("/library/manifest")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No se pudo cargar la biblioteca"))
      .finally(() => setLoading(false));
  }, [user, authLoading, canAccess, nav]);

  const byCategory = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const c of data.categories) g[c.id] = [];
    for (const d of data.documents) {
      if (!g[d.category_id]) g[d.category_id] = [];
      g[d.category_id].push(d);
    }
    return g;
  }, [data]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, downloaded: 0, updated: 0 };
    const total = data.documents.filter((d) => d.status !== "coming" && d.status !== "empty").length;
    const downloaded = data.documents.filter((d) => d.downloaded_at).length;
    const updated = data.documents.filter((d) => d.status === "updated").length;
    return { total, downloaded, updated };
  }, [data]);

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <CircleNotch size={32} className="animate-spin text-as-green" />
        </div>
      </Layout>
    );
  }

  if (!canAccess) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center bg-as-black px-4">
          <div className="max-w-md w-full text-center border border-as-border p-8 bg-as-surface">
            <Lock size={40} weight="duotone" color="#39FF14" className="mx-auto mb-4" />
            <p className="as-label text-as-green mb-2">BIBLIOTECA PERSONAL</p>
            <h1 className="as-heading text-2xl text-white mb-3">EXCLUSIVA ELITE / EVOLUTION</h1>
            <p className="text-sm text-as-gray leading-relaxed mb-6">
              Actualiza tu plan para acceder a tu biblioteca de recursos: fichas de ejercicios,
              guías de suplementación y nutrición, evaluaciones y planes personalizados.
            </p>
            <Link to="/planes" className="inline-block px-5 py-3 bg-as-green text-as-black font-bold text-xs uppercase tracking-wider hover:bg-white transition-colors" data-testid="library-upgrade">
              Ver planes disponibles →
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-start gap-3">
            <Sparkle size={32} weight="duotone" color="#39FF14" className="flex-shrink-0 mt-1" />
            <div>
              <p className="as-label text-as-green mb-3">TU BIBLIOTECA</p>
              <h1 className="as-heading text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="library-title">
                MIS <span className="text-as-green">DESCARGAS.</span>
              </h1>
              <p className="text-as-gray mt-4 max-w-xl">
                Tu biblioteca de conocimiento AS TRAINING. Todos tus recursos exclusivos organizados en un solo lugar.
                Vuelve a descargarlos cuando quieras mientras tu plan esté activo.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8 max-w-2xl">
            <StatBox label="DOCUMENTOS" value={stats.total} />
            <StatBox label="DESCARGADOS" value={stats.downloaded} />
            <StatBox label="ACTUALIZACIONES" value={stats.updated} highlight />
          </div>
        </div>
      </section>

      {error && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
        </div>
      )}

      {/* Categorías */}
      <section className="bg-as-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {data?.categories.map((cat) => {
            const docs = byCategory[cat.id] || [];
            const CatIcon = ICONS[cat.icon] || Barbell;
            return (
              <div key={cat.id} data-testid={`library-cat-${cat.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-as-black border border-as-green/30 flex items-center justify-center flex-shrink-0">
                    <CatIcon size={20} weight="duotone" color="#39FF14" />
                  </div>
                  <div>
                    <h2 className="as-heading text-xl text-white">{cat.label.toUpperCase()}</h2>
                    <p className="text-[10px] text-as-gray uppercase tracking-widest">
                      {docs.length} {docs.length === 1 ? "documento" : "documentos"}
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {docs.length === 0 ? (
                    <EmptyDoc />
                  ) : (
                    docs.map((d) => <DocCard key={d.id} doc={d} />)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`p-3 border ${highlight && value > 0 ? "border-as-green bg-as-green/10" : "border-as-border bg-as-surface"}`}>
      <p className="as-label text-as-green text-[9px] mb-1">{label}</p>
      <p className="text-2xl text-white font-bold">{value}</p>
    </div>
  );
}

function EmptyDoc() {
  return (
    <div className="col-span-full p-6 text-center text-xs text-as-gray bg-as-black border border-as-border">
      Aún no hay documentos en esta categoría. Completa tu evaluación o adquiere un plan personalizado.
    </div>
  );
}

function DocCard({ doc }) {
  const [loading, setLoading] = useState(false);
  const isNew = doc.status === "new";
  const isUpdated = doc.status === "updated";
  const isReady = doc.status === "ready";
  const isComing = doc.status === "coming";
  const isEmpty = doc.status === "empty";

  const Icon = ICONS[doc.icon] || FileText;

  const handleClick = async () => {
    const dl = doc.download;
    // Hub: navigate to page
    if (dl.type === "hub") {
      window.location.href = dl.path;
      return;
    }
    // GET dynamic (user's own file, e.g. evaluation PDF)
    if (dl.type === "get") {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${dl.endpoint}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Error al descargar");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${doc.id}_AS_TRAINING.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        // Track
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/library/track/${doc.id}`, {
          method: "POST",
          credentials: "include",
        }).catch(() => {});
        toast.success("Descargado", { description: doc.title });
      } catch (e) {
        toast.error(e.message || "No se pudo descargar");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isComing || isEmpty) {
      toast.info("Próximamente disponible", { description: doc.description });
    }
  };

  return (
    <div
      className={`bg-as-black border p-5 transition-colors ${
        isUpdated ? "border-as-green" : "border-as-border hover:border-as-green"
      }`}
      data-testid={`library-doc-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <Icon size={28} weight="duotone" color="#39FF14" />
        <StatusBadge status={doc.status} />
      </div>
      <h3 className="as-heading text-base text-white mb-2 leading-tight">{doc.title}</h3>
      <p className="text-xs text-as-gray leading-relaxed mb-4">{doc.description}</p>

      <div className="text-[10px] text-as-gray space-y-1 mb-4 pt-3 border-t border-as-border">
        <div className="flex justify-between">
          <span className="uppercase tracking-wider">Actualizado</span>
          <span className="text-white">{doc.updated_at}</span>
        </div>
        {doc.downloaded_at && (
          <div className="flex justify-between">
            <span className="uppercase tracking-wider">Última descarga</span>
            <span className="text-white">{doc.downloaded_at.slice(0, 10)}</span>
          </div>
        )}
        {typeof doc.version === "number" && doc.version > 0 && !isComing && (
          <div className="flex justify-between">
            <span className="uppercase tracking-wider">Versión</span>
            <span className="text-white">v{doc.version}</span>
          </div>
        )}
      </div>

      <button
        onClick={handleClick}
        disabled={loading || isComing || isEmpty}
        className={`w-full flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-wider font-bold transition-colors ${
          isComing || isEmpty
            ? "bg-as-surface text-as-gray cursor-not-allowed"
            : isUpdated
            ? "bg-as-green text-as-black hover:bg-white"
            : "bg-as-surface text-white border border-as-border hover:border-as-green hover:text-as-green"
        }`}
        data-testid={`library-action-${doc.id}`}
      >
        {loading ? (
          <CircleNotch size={12} weight="bold" className="animate-spin" />
        ) : isComing ? (
          <>
            <Clock size={12} weight="bold" /> Próximamente
          </>
        ) : isEmpty ? (
          <>
            <Warning size={12} weight="bold" /> Completa tu evaluación
          </>
        ) : doc.download.type === "hub" ? (
          <>
            Abrir <ArrowRight size={12} weight="bold" />
          </>
        ) : (
          <>
            <DownloadSimple size={12} weight="bold" />
            {isUpdated ? "Descargar nueva versión" : doc.downloaded_at ? "Volver a descargar" : "Descargar"}
          </>
        )}
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: { label: "Nuevo", cls: "bg-as-green text-as-black" },
    updated: { label: "Nueva versión", cls: "bg-as-green text-as-black animate-pulse" },
    ready: { label: "Descargado", cls: "bg-as-surface text-as-gray border border-as-border" },
    coming: { label: "Próximamente", cls: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40" },
    empty: { label: "Sin datos", cls: "bg-as-surface text-as-gray border border-as-border" },
  };
  const s = map[status] || map.ready;
  return <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 ${s.cls}`}>{s.label}</span>;
}
